import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import { useModal } from './useModal';
import { readViews, removeView, saveView } from '../savedViews';
import { csvName, downloadCsv, toCsv } from '../csv';

/**
 * The things every list in this admin was missing, in one strip.
 *
 * All of them exist because a search here is already a URL — the pages read
 * their filters from the query string — so none needed an endpoint, a schema or
 * a migration.
 *
 *   - **Copy link.** A filtered list is the answer to a question somebody asked
 *     in Slack, and the only way to pass it on was to describe the filters and
 *     hope. Now it is a link.
 *   - **Export.** Not the four report endpoints — those answer a business
 *     question over a date range. This is the other thing people want: *the
 *     list they are looking at*, in a spreadsheet. It was being done by
 *     selecting the table and pasting, which loses every id, because ids render
 *     short. This exports what the page fetched, so they come out whole.
 *   - **Export all matching**, where the page can fetch more: "every signup for
 *     this facility this month" is the real request, and fifteen rows is not it.
 *     Capped, and the file says so when a cap stopped it.
 *   - **Saved searches.** The daily cost this removes is real: the same four
 *     filters retyped every morning, because there was nowhere to keep them.
 *     Local to the browser, per screen, and they show up in ⌘K.
 *
 * Rendered as a quiet row rather than four more buttons in the header: none of
 * them is the reason anybody came to the page.
 */

// Walking a whole result set is N requests, and a filter nobody set can mean
// hundreds of thousands of rows. Two caps, whichever comes first, and the file
// says when one of them stopped it — a truncated export that does not say so is
// worse than no export.
const MAX_EXPORT_PAGES = 40;
const MAX_EXPORT_ROWS = 5000;

export default function ListTools({ screenPath, search, columns, rows, exportName, fetchPage, perPage }) {
  const { ask, modalProps } = useModal();

  const [views, setViews] = useState(() => readViews(screenPath));
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    setViews(readViews(screenPath));
  }, [screenPath]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = event => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}${search}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (error) {
      // Clipboard is blocked in some contexts; the address bar still has it.
    }
  };

  const exportCsv = () => downloadCsv(csvName(exportName), toCsv(columns, rows || []));

  /**
   * The rest of the result, not just the page on screen.
   *
   * "Every signup for this facility this month" is the export people actually
   * want, and one page of fifteen is not it. It walks forward until a page comes
   * back short — which is how these endpoints say "that was the last one" — or
   * until a cap stops it.
   */
  const exportAll = async () => {
    const collected = [...(rows || [])];
    let stoppedAtCap = false;

    setExporting(collected.length);
    try {
      // Forward one page at a time until a short page says "that was the last
      // one", or a cap stops it. Sequential on purpose: this is one person's
      // export, not a reason to put forty simultaneous queries on the database.
      for (let page = 2; page <= MAX_EXPORT_PAGES; page += 1) {
        if (collected.length >= MAX_EXPORT_ROWS) {
          stoppedAtCap = true;
          break;
        }

        // eslint-disable-next-line no-await-in-loop
        const next = await fetchPage(page);
        if (!Array.isArray(next) || next.length === 0) break;

        collected.push(...next);
        setExporting(collected.length);

        if (next.length < perPage) break;
        if (page === MAX_EXPORT_PAGES) stoppedAtCap = true;
      }

      // A truncated export that does not say so is worse than no export: the
      // note is a row in the file, where whoever opens it will see it.
      const csv = stoppedAtCap
        ? `${toCsv(columns, collected)}\r\n"Stopped at ${collected.length} rows — this export is capped. Narrow the filters to get the rest."`
        : toCsv(columns, collected);

      downloadCsv(csvName(`${exportName}-all`), csv);
    } catch (error) {
      // Whatever was collected is still worth having, under a name that says it
      // is not everything.
      if (collected.length) downloadCsv(csvName(`${exportName}-partial`), toCsv(columns, collected));
    } finally {
      setExporting(null);
    }
  };

  const save = () => ask({
    kind: 'input',
    title: 'Name this search',
    context: 'Saved searches',
    body: 'The filters currently applied are kept under this name, in this browser. It is a shortcut, not a report — nothing is stored on the server and nobody else sees it.',
    label: 'Name',
    placeholder: 'Failed signups today',
    confirmLabel: 'Save',
    onConfirm: name => {
      const trimmed = String(name || '').trim();
      if (!trimmed) throw new Error('a name is needed');
      setViews(saveView(screenPath, trimmed, search));
    }
  });

  const count = (rows || []).length;

  return (
    <div className="next-listtools">
      <div className="next-listtools__saved" ref={boxRef}>
        <button
          type="button"
          className="next-btn next-btn--sm"
          aria-expanded={open}
          onClick={() => setOpen(value => !value)}
        >
          Saved searches{views.length ? ` (${views.length})` : ''}
        </button>

        {open && (
          <div className="next-listtools__menu">
            {views.length === 0 && (
              <p className="next-listtools__empty">
                Nothing saved for this list yet. Set the filters you want, then Save this search.
              </p>
            )}
            {views.map(view => (
              <div className="next-listtools__view" key={view.name}>
                {/* A plain anchor, not a router link: the whole point is to
                    replace the query string, and going through the router with
                    the same pathname is the case react-router treats as a
                    no-op. */}
                <a className="next-listtools__viewlink" href={`${window.location.pathname}${view.search}`}>
                  {view.name}
                  {!view.search && <span className="next-listtools__all"> · no filters</span>}
                </a>
                <button
                  type="button"
                  className="next-listtools__forget"
                  aria-label={`Forget ${view.name}`}
                  onClick={() => setViews(removeView(screenPath, view.name))}
                >
                  ×
                </button>
              </div>
            ))}

            <button type="button" className="next-listtools__save" onClick={() => { setOpen(false); save(); }}>
              Save this search
            </button>
          </div>
        )}
      </div>

      <button type="button" className="next-btn next-btn--sm" onClick={copyLink}>
        {copied ? 'Link copied' : 'Copy link'}
      </button>

      <button
        type="button"
        className="next-btn next-btn--sm"
        disabled={!count || exporting !== null}
        title={count ? undefined : 'Nothing on screen to export'}
        onClick={exportCsv}
      >
        {/* Says what it will contain, because a page is not the whole result. */}
        Export {count ? `${count} row${count === 1 ? '' : 's'}` : 'CSV'}
      </button>

      {/* Only where the page can fetch another one, and only when there plainly
          is another one — a full page is how these endpoints say "there may be
          more". */}
      {fetchPage && count >= perPage && (
        <button
          type="button"
          className="next-btn next-btn--sm"
          disabled={exporting !== null}
          onClick={exportAll}
        >
          {exporting === null ? 'Export all matching' : `Collecting ${exporting}…`}
        </button>
      )}

      <Modal {...modalProps} />
    </div>
  );
}

ListTools.propTypes = {
  // Which list these searches belong to, e.g. `/next/signups`.
  screenPath: PropTypes.string.isRequired,
  // The current query string, `?a=b` or ''.
  search: PropTypes.string,
  // `[key, label]` pairs — the screen's own columns.
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array,
  exportName: PropTypes.string.isRequired,
  // `page => Promise<rows>` for the same filters. Omitted where the page cannot
  // fetch a second page, and the whole-result export is then not offered.
  fetchPage: PropTypes.func,
  perPage: PropTypes.number
};
